/**
 * Dev impersonation sign-in card — local role testing only.
 *
 * Calls GET /api/auth/devLogin/refresh?username=… and builds a UserIdentity
 * from the issued token. Visible only when auth.environment=development,
 * digitalFactory.features.devImpersonation, and auth.providers.devLogin.users
 * are configured (app-config.local.yaml).
 */
import { useEffect, useState } from 'react';
import { UserIdentity } from '@backstage/core-components';
import type { IdentityApi, ProfileInfo } from '@backstage/core-plugin-api';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  makeStyles,
} from '@material-ui/core';
import DeveloperModeOutlinedIcon from '@material-ui/icons/DeveloperModeOutlined';
import { boschBlue, boschGray, boschRed, boschWhite } from '../../modules/theme/boschColors';

export type DevUser = {
  name: string;
  displayName?: string;
};

type DevImpersonationCardProps = {
  users: DevUser[];
  discoveryApi: { getBaseUrl: (pluginId: string) => Promise<string> };
  onSignInSuccess: (identityApi: IdentityApi) => void;
};

const useCardStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    borderRadius: 16,
    border: `1px solid ${boschBlue[90]}`,
    backgroundColor: boschWhite,
    boxShadow: `0 12px 40px ${boschBlue[80]}22`,
  },
  cardContent: {
    padding: theme.spacing(3),
  },
  iconWrap: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    backgroundColor: boschBlue[95],
    color: boschBlue[50],
  },
  cardTitle: {
    fontWeight: 700,
    color: boschBlue[20],
    marginBottom: theme.spacing(1),
  },
  message: {
    color: boschGray[50],
    marginBottom: theme.spacing(2),
  },
  select: {
    marginTop: theme.spacing(1),
  },
  error: {
    marginTop: theme.spacing(1.5),
    color: boschRed[50],
  },
  actions: {
    padding: theme.spacing(0, 3, 3),
  },
  submitButton: {
    textTransform: 'none',
    fontWeight: 600,
  },
}));

export const DevImpersonationCard = ({
  users,
  discoveryApi,
  onSignInSuccess,
}: DevImpersonationCardProps) => {
  const classes = useCardStyles();
  const [selected, setSelected] = useState<string>(users[0]?.name ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!selected && users[0]) setSelected(users[0].name);
  }, [users, selected]);

  const signIn = async () => {
    if (!selected) return;
    setError(undefined);
    setLoading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('auth');
      const url = `${baseUrl}/devLogin/refresh?username=${encodeURIComponent(
        selected,
      )}`;
      const res = await fetch(url, {
        method: 'GET',
        credentials: 'include',
      });

      if (!res.ok) {
        const body = await res.text();
        throw new Error(
          `dev-login failed: ${res.status} ${res.statusText} — ${body}`,
        );
      }

      const json = (await res.json()) as {
        profile: ProfileInfo;
        backstageIdentity: {
          token: string;
          identity: {
            type: 'user';
            userEntityRef: string;
            ownershipEntityRefs: string[];
          };
        };
      };

      const identityApi = UserIdentity.create({
        identity: json.backstageIdentity.identity,
        profile: json.profile,
        authApi: {
          getProfile: async () => json.profile,
          getProfileInfo: async () => json.profile,
          getBackstageIdentity: async () => ({
            ...json.backstageIdentity.identity,
            token: json.backstageIdentity.token,
            expiresInSeconds: undefined as unknown as number,
          }),
          getCredentials: async () => ({ token: json.backstageIdentity.token }),
          signOut: async () => {},
          sessionState$: () =>
            ({
              subscribe: () => ({ unsubscribe: () => {} }),
            }) as any,
        } as any,
      });

      onSignInSuccess(identityApi);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="dev-login-card">
      <Card className={classes.card} elevation={0}>
        <CardContent className={classes.cardContent}>
          <div className={classes.iconWrap} aria-hidden>
            <DeveloperModeOutlinedIcon />
          </div>
          <Typography variant="h6" component="h2" className={classes.cardTitle}>
            Dev impersonation
          </Typography>
          <Typography variant="body2" className={classes.message}>
            Sign in as a ManagedUsers persona to test RBAC roles locally.
            Disabled outside development.
          </Typography>

          <FormControl
            variant="outlined"
            fullWidth
            size="small"
            className={classes.select}
          >
            <InputLabel id="dev-login-user-label">Sign in as</InputLabel>
            <Select
              labelId="dev-login-user-label"
              label="Sign in as"
              value={selected}
              onChange={e => setSelected(String(e.target.value))}
              inputProps={{ 'data-testid': 'dev-login-user-select' }}
              SelectDisplayProps={{
                ['data-testid' as string]: 'dev-login-user-trigger',
              }}
            >
              {users.map(u => (
                <MenuItem
                  key={u.name}
                  value={u.name}
                  data-testid={`dev-login-user-option-${u.name}`}
                >
                  {u.displayName ?? u.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {error && (
            <Typography
              variant="body2"
              className={classes.error}
              data-testid="dev-login-error"
            >
              {error}
            </Typography>
          )}
        </CardContent>
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            variant="contained"
            disabled={!selected || loading}
            onClick={signIn}
            className={classes.submitButton}
            data-testid="dev-login-submit"
            startIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
        </CardActions>
      </Card>
    </div>
  );
};
